import { Fragment, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDays, format, isBefore, isValid, parse } from 'date-fns';
import {
  AlertCircleIcon,
  ArrowUpRightIcon,
  CheckIcon,
  CopyCheckIcon,
  CopyIcon,
  EditIcon,
  ExternalLinkIcon,
  FilePlus2Icon,
  PlusIcon,
  TrophyIcon,
  XIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { DialogDeleteRecordConfirmation } from '../../../components/pages/backoffice/dialogs/delete-record.jsx';
import DialogExportRecords from '../../../components/pages/backoffice/dialogs/export-records.jsx';
import WinnerInfo from '../../../components/pages/backoffice/winner-info.jsx';
import DecorativeInput from '../../../components/decorative-input.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import Heading from '../../../components/typography/heading.jsx';
import BadgeList from '../../../components/ui/badge-list.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Card } from '../../../components/ui/card.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import { DateTimeInputWithDatePicker } from '../../../components/ui/date-time-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import RichTextViewer from '../../../components/ui/rich-text-viewer.jsx';
import { Textarea } from '../../../components/ui/textarea.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { queryClient } from '../../../services/query/client.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { useClipboard } from '../../../hooks/use-clipboard.jsx';
import { cn } from '../../../utils/cn.js';
import {
  CAMPAIGN_BANNER_DIMENSIONS,
  CAMPAIGN_GENERATE_WINNERS_STATES,
  CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS,
  CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS,
  CAMPAIGN_STATES,
} from '../../../utils/constants.js';
import { getExportFile } from '../../../utils/files.js';
import { math } from '../../../utils/math.js';
import { REGEX_DATETIME_EU } from '../../../utils/regex.js';
import { scrollToRef } from '../../../utils/scroll.js';
import { campaignUrl } from '../../../utils/urls.js';

const initalFormValues = {
  cronDate: '',
  confrimation: false,
};

export function CampaignsShow() {
  const { campaignId } = useParams();
  const { isAdmin } = useSnapshot(authState);
  const clipboard = useClipboard({ timeout: 2000 });

  const [generateWinnersFormOpen, setGenerateWinnersFormOpen] = useState(false);

  const refWinnerCard = useRef(null);

  const queryCampaign = useQuery({
    queryKey: ['campaign'],
    queryFn: async () => {
      const { data: campaign } = await msCatalystApi.get(
        `/campaigns/${campaignId}`,
      );
      const campaignData = campaign.data;

      if (campaignData?.campaign.winners) {
        campaignData.winners = JSON.parse(campaignData.campaign.winners);
      }

      if (campaignData?.campaign.winnersSubstitutes) {
        campaignData.campaign.winnersSubstitutes = JSON.parse(
          campaignData.campaign.winnersSubstitutes,
        );
      }

      // format dates
      campaignData.campaign.startDate = format(
        campaignData.campaign.startDate,
        'dd-MM-yyyy HH:mm:ss',
      );
      campaignData.campaign.closeDate = format(
        campaignData.campaign.closeDate,
        'dd-MM-yyyy HH:mm:ss',
      );
      campaignData.campaign.endDate = format(
        campaignData.campaign.endDate,
        'dd-MM-yyyy HH:mm:ss',
      );

      return campaignData;
    },
    refetchInterval: (data) => {
      const status = data?.state?.data?.campaign?.generateWinnersStatus;

      return status === CAMPAIGN_GENERATE_WINNERS_STATES.ONGOING
        ? 30000
        : false;
    },
  });

  const mutationDeleteCampaign = useMutation({
    mutationFn: async () => {
      await msCatalystApi.delete(`/campaigns/${campaignId}`);
    },
  });

  /*
  const mutationGenerateCampaignWinners = useMutation({
    mutationFn: async ({ campaignId, cronDate }) => {
      const { data: campaignsToDrawWinners } = await msCatalystApi.post(
        `/campaigns/generate-winners/${campaignId}`,
        { cron_date: cronDate },
      );

      return campaignsToDrawWinners.data;
    },
    onSuccess: async (data) => {
      toast.success('Vencedor(es) da campanha gerado(s) com sucesso');

      await queryClient.refetchQueries({ queryKey: ['campaign'] });
    },
    onError: (err) => {
      toast.error('Erro a gerar vencedor(es) da campanha', {
        description: 'Tente novamente mais tarde',
      });

      formGenerateCampaignWinners.reset();
    },
    onSettled: () => {
      setGenerateWinnersFormOpen(false);
    },
  });
  */

  const next_mutationGenerateCampaignWinners = useMutation({
    mutationFn: async ({ campaignId, cronDate }) => {
      const { data: campaignsToDrawWinners } = await msCatalystApi.post(
        `/campaigns/generate-winners/next/${campaignId}`,
        { cron_date: cronDate },
      );

      return campaignsToDrawWinners.data;
    },
    onSuccess: async (data) => {
      toast.success(
        'Processo de sorteio de Vencedor(es) da campanha iniciado com sucesso',
      );

      await queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
    onError: (err) => {
      toast.error('Erro a gerar vencedor(es) da campanha', {
        description: 'Tente novamente mais tarde',
      });

      formGenerateCampaignWinners.reset();
    },
    onSettled: () => {
      setGenerateWinnersFormOpen(false);
    },
  });

  const mutationExportWinnersData = useMutation({
    mutationFn: async ({ fileType, winnersType }) => {
      try {
        return getExportFile(
          `/campaigns/export/winners/${campaignId}`,
          'campaigns',
          {
            fileType,
            winnersType,
          },
        );
      } catch (error) {
        throw error;
      }
    },
    onError: () => {
      toast.error('Erro na exportação', {
        description: 'Tente novamente mais tarde',
      });
    },
    onSuccess: () => {
      toast.success('Exportação realizada com sucesso', {
        description: 'Foi automaticamente realizado o download do ficheiro',
      });
    },
  });

  const formGenerateCampaignWinners = useForm({
    mode: 'onChange',
    values: {
      ...initalFormValues,
    },
    resolver: yupResolver(
      yup.object().shape({
        cronDate: yup
          .string()
          .required(
            'Data de Limpeza Automática de Clientes é um campo obrigatório',
          )
          .matches(
            REGEX_DATETIME_EU,
            'Data de Limpeza Automática de Clientes não possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isOneDayFuture',
            'A data deve ser pelo menos um dia a partir de hoje',
            function (value) {
              if (!value) return true;

              try {
                const currentDate = new Date();
                const parsedDate = parse(
                  value,
                  'dd-MM-yyyy HH:mm:ss',
                  currentDate,
                );

                const minDate = addDays(currentDate, 1);

                return !isBefore(parsedDate, minDate);
              } catch (error) {
                return false;
              }
            },
          ),
        confirmation: yup
          .boolean(true)
          .oneOf(
            [true],
            'Para prosseguir com a geração de vencedores é necessário a sua confirmação',
          ),
      }),
    ),
  });

  const onFormSubmit = async (data) => {
    await next_mutationGenerateCampaignWinners.mutateAsync({
      campaignId,
      ...data,
    });
  };

  if (queryCampaign.isLoading) {
    return (
      <div className="flex h-[70dvh] items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (queryCampaign.isError || queryCampaign.isRefetchError) {
    return <Error />;
  }

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <div>
          <Heading className="pb-2">Campanha</Heading>
          <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
            {queryCampaign.data.campaign.name}
          </h2>
        </div>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          {isAdmin ? (
            <DialogDeleteRecordConfirmation
              recordName={queryCampaign.data.campaign.name}
              redirect="/backoffice/campaigns"
              action={async () => mutationDeleteCampaign.mutateAsync()}
            />
          ) : null}

          {isAdmin ? (
            <Link to={`/backoffice/campaigns/edit/${campaignId}`}>
              <Button>
                <EditIcon />
                Editar
              </Button>
            </Link>
          ) : null}

          {queryCampaign.data.campaign.state === CAMPAIGN_STATES.ONGOING ? (
            <Link to="/backoffice/invoices/create">
              <Button>
                <FilePlus2Icon />
                Inserir Fatura
              </Button>
            </Link>
          ) : null}

          {isAdmin &&
          queryCampaign.data.campaign.state ===
            CAMPAIGN_STATES.READY_TO_DRAW ? (
            <Link>
              <Button onClick={() => scrollToRef(refWinnerCard)}>
                <TrophyIcon />
                Gerar Vencedor(es)
              </Button>
            </Link>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col space-y-10">
        <div className="flex flex-col gap-5">
          <FormCard>
            <DecorativeInput
              className="col-span-1 lg:col-span-2"
              value={queryCampaign.data.campaign.name}
              label="Nome"
            />

            <DecorativeInput
              className="col-span-1 lg:col-span-2"
              value={queryCampaign.data.campaign.state}
              label="Estado"
            />

            <DecorativeInput
              value={queryCampaign.data.campaign.startDate}
              label="Data de Início"
            />

            <DecorativeInput
              value={queryCampaign.data.campaign.closeDate}
              label="Data de Fecho de Participações"
            />

            <DecorativeInput
              value={queryCampaign.data.campaign.endDate}
              label="Data de Fim"
            />

            <DecorativeInput
              value={queryCampaign.data.campaign.numOfWinners}
              label="Nº de Vencedores"
            />

            <DecorativeInput
              value={queryCampaign.data.campaign.numOfSubstituteWinners}
              label="Nº de Suplentes"
            />

            <DecorativeInput
              value={queryCampaign.data.campaign.minEntryAmount}
              label="Valor Minimo de Participação (€)"
            />

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Frase Legal
              </p>
              <Textarea
                readOnly={true}
                value={queryCampaign.data.campaign.legalPhrase}
              />
            </div>

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Condições da Campanha
              </p>
              <RichTextViewer value={queryCampaign.data.campaign.description} />
            </div>

            {queryCampaign?.data?.campaign?.regulationUrl ? (
              <div className="col-span-1 flex flex-col lg:col-span-2">
                <p className="mb-3 inline-block text-sm font-medium leading-none">
                  Regulamento (Ficheiro)
                </p>
                <a
                  href={queryCampaign?.data?.campaign?.regulationUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Button>
                    <ExternalLinkIcon className="size-4" />
                    Ver PDF
                  </Button>
                </a>
              </div>
            ) : null}

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Regulamento (Descrição)
              </p>
              <RichTextViewer value={queryCampaign.data.campaign.rules} />
            </div>

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Lojas Aderentes
              </p>

              <BadgeList
                items={queryCampaign.data.shops.map((item) => ({
                  status: item.status,
                  label: item.name,
                  value: item.ROWID,
                }))}
                status={{
                  key: 'status',
                  colors: { true: 'bg-green-400', false: 'bg-red-400' },
                }}
              />
            </div>

            <div className="col-span-1 flex flex-row items-center space-x-2 lg:col-span-2">
              <Checkbox
                className="mr-1 size-5"
                disabled={true}
                checked={queryCampaign.data.campaign.adherentShopsVisible}
              />
              <p className="ml-4 space-y-0 text-sm font-medium leading-none">
                Lojas Aderentes visivéis no Portal do Cliente?
              </p>
            </div>

            <div className="col-span-1 flex flex-row items-center space-x-2 lg:col-span-2">
              <Checkbox
                className="mr-1 size-5"
                disabled={true}
                checked={queryCampaign.data.campaign.showOrderedWinners}
              />
              <p className="ml-4 space-y-0 text-sm font-medium leading-none">
                Vencedores da Campanha serão apresentados por ordem sequencial?
                (ex. 1º, 2º, 3º)?
              </p>
            </div>
          </FormCard>
        </div>

        <div className="flex flex-col gap-5">
          <h2 className="text-xl font-medium">Página da Campanha</h2>

          <FormCard>
            <div className="col-span-1 lg:col-span-2">
              <DecorativeInput
                className="overflow-auto text-wrap"
                value={campaignUrl(queryCampaign.data.campaign.name)}
                label="Link Microsite"
              />

              <div className="mt-2 flex flex-row flex-wrap gap-2">
                <Button
                  variant="ghost"
                  type="button"
                  disabled={clipboard.copied}
                  className={cn(
                    clipboard.copied && 'text-green-700 dark:!text-green-300',
                  )}
                  onClick={() =>
                    clipboard.copy(
                      campaignUrl(queryCampaign.data.campaign.name),
                    )
                  }
                >
                  {clipboard.copied ? (
                    <>
                      <CopyCheckIcon className="size-3" />
                      Link Copiado
                    </>
                  ) : (
                    <>
                      <CopyIcon className="size-3" />
                      Copiar
                    </>
                  )}
                </Button>

                <Link
                  target="_blank"
                  to={campaignUrl(queryCampaign.data.campaign.name)}
                >
                  <Button variant="ghost" type="button">
                    <ExternalLinkIcon className="size-3" />
                    <span>Abrir Link</span>
                  </Button>
                </Link>
              </div>
            </div>

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Banner Microsite ({CAMPAIGN_BANNER_DIMENSIONS.width}x
                {CAMPAIGN_BANNER_DIMENSIONS.height})
              </p>
              <div className="flex h-auto items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <span className="cursor-default break-all">
                  {queryCampaign?.data?.campaign.bannerUrl}
                </span>

                <div>
                  <a
                    href={queryCampaign?.data?.campaign.bannerUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Button
                      type="button"
                      className="h-auto p-2 text-xs"
                      variant="ghost"
                    >
                      <ExternalLinkIcon className="size-4" />
                      Ver Imagem
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Imagem de Campanha Inativa - Ecrãs Pequenos (
                {CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS.width}x
                {CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS.height})
              </p>
              <div className="flex h-auto items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <span className="cursor-default break-all">
                  {queryCampaign?.data?.campaign.heroNotActiveSmUrl}
                </span>

                <div>
                  <a
                    href={queryCampaign?.data?.campaign.heroNotActiveSmUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Button
                      type="button"
                      className="h-auto p-2 text-xs"
                      variant="ghost"
                    >
                      <ExternalLinkIcon className="size-4" />
                      Ver Imagem
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            <div className="col-span-1 lg:col-span-2">
              <p className="mb-3 inline-block text-sm font-medium leading-none">
                Imagem de Campanha Inativa - Ecrãs Grandes (
                {CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS.width}x
                {CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS.height})
              </p>
              <div className="flex h-auto items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <span className="cursor-default break-all">
                  {queryCampaign?.data?.campaign.heroNotActiveXlUrl}
                </span>

                <div>
                  <a
                    href={queryCampaign?.data?.campaign.heroNotActiveXlUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Button
                      type="button"
                      className="h-auto p-2 text-xs"
                      variant="ghost"
                    >
                      <ExternalLinkIcon className="size-4" />
                      Ver Imagem
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </FormCard>
        </div>

        <div className="flex flex-col gap-5">
          <h2 className="text-xl font-medium">Participações na Campanha</h2>

          <FormCard className="flex items-center gap-4">
            <Link
              target="_blank"
              to={`/backoffice/coupons?campaignId=${queryCampaign?.data?.campaign.ROWID}`}
            >
              <Button variant="secondary">
                <span>Ver cupões</span>
                <ArrowUpRightIcon />
              </Button>
            </Link>
            <Link
              target="_blank"
              to={`/backoffice/invoices?campaignId=${queryCampaign?.data?.campaign.ROWID}`}
            >
              <Button variant="secondary">
                <span>Ver faturas</span>
                <ArrowUpRightIcon />
              </Button>
            </Link>
          </FormCard>
        </div>

        {isAdmin ? (
          <div className="flex flex-col gap-5" ref={refWinnerCard}>
            <h2 className="text-xl font-medium">Vencedor(es)</h2>

            <FormCard>
              {queryCampaign.isRefetching ||
              queryCampaign.data.campaign.generateWinnersStatus ===
                CAMPAIGN_GENERATE_WINNERS_STATES.ONGOING ? (
                <div className="col-span-1 flex w-full justify-center lg:col-span-2">
                  <Loading message="A gerar vencedores, este processo pode demorar alguns minutos. Por favor aguarde" />
                </div>
              ) : next_mutationGenerateCampaignWinners.isPending ? (
                <div className="col-span-1 flex w-full justify-center lg:col-span-2">
                  <Loading message="A iniciar processo de gerar vencedores" />
                </div>
              ) : next_mutationGenerateCampaignWinners.isError ? (
                <Error
                  className="col-span-1 lg:col-span-2"
                  errorMessage="Erro ao iniciar processo de gerar vencedores"
                />
              ) : queryCampaign.data.campaign.state ===
                CAMPAIGN_GENERATE_WINNERS_STATES.ERROR ? (
                <div className="col-span-1 flex flex-col items-center text-center text-orange-500 lg:col-span-2">
                  <AlertCircleIcon className="size-20" />
                  <h3 className="pt-4 text-xl font-medium">
                    A campanha ainda não está a decorrer sendo impossível gerar
                    o vencedor da mesma
                  </h3>
                </div>
              ) : queryCampaign.data.campaign.state ===
                CAMPAIGN_STATES.YET_TO_START ? (
                <div className="col-span-1 flex flex-col items-center text-center text-orange-500 lg:col-span-2">
                  <AlertCircleIcon className="size-20" />
                  <h3 className="pt-4 text-xl font-medium">
                    A campanha ainda não começou, pelo que ainda não é possível
                    gerar vencedores
                  </h3>
                </div>
              ) : queryCampaign.data.campaign.state ===
                  CAMPAIGN_STATES.ONGOING ||
                queryCampaign.data.campaign.state ===
                  CAMPAIGN_STATES.POST_END_DATE ? (
                <div className="col-span-1 flex flex-col items-center text-center text-orange-500 lg:col-span-2">
                  <AlertCircleIcon className="size-20" />
                  <h3 className="pt-4 text-xl font-medium">
                    A campanha ainda está a decorrer sendo impossível gerar o
                    vencedor da mesma
                  </h3>
                </div>
              ) : queryCampaign.data.campaign.state ===
                CAMPAIGN_STATES.READY_TO_DRAW ? (
                <div className="col-span-1 flex flex-col items-center text-center lg:col-span-2">
                  {!generateWinnersFormOpen ? (
                    <>
                      <AlertCircleIcon className="size-20" />

                      <h3 className="pt-1 text-xl font-medium">
                        A campanha encontra-se fechada ao público e pronta para
                        ser sorteada.
                      </h3>

                      <p className="pb-8 pt-2 text-stone-500">
                        Pode agora gerar os vencedores e os suplentes
                        aleatoriamente.
                      </p>

                      <Button
                        onClick={() => {
                          setGenerateWinnersFormOpen(true);
                        }}
                      >
                        <TrophyIcon />
                        Gerar Vencedor(es)
                      </Button>
                    </>
                  ) : (
                    <Form {...formGenerateCampaignWinners}>
                      <form
                        onSubmit={formGenerateCampaignWinners.handleSubmit(
                          onFormSubmit,
                        )}
                      >
                        <h2 className="text-2xl font-bold text-red-500">
                          Tem a certeza?
                        </h2>
                        <p className="text-base font-medium text-red-500">
                          O processo de geração de vencedor(es) é irreversível
                        </p>

                        <div className="my-8 flex flex-col items-start justify-start gap-4">
                          <FormField
                            control={formGenerateCampaignWinners.control}
                            name="cronDate"
                            render={({ field }) => {
                              let selectedDate = null;

                              if (field?.value?.match(REGEX_DATETIME_EU)) {
                                const [dateStr, hoursStr] =
                                  field.value.split(' ');
                                const [day, month, year] = dateStr.split('-');
                                const [hours, minutes, seconds] =
                                  hoursStr.split(':');

                                selectedDate = new Date(
                                  `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                                );
                              }

                              return (
                                <FormItem>
                                  <FormLabel
                                    required={true}
                                    className="block w-full text-left"
                                  >
                                    Data de Limpeza Automática de Clientes
                                  </FormLabel>
                                  <FormControl>
                                    <DateTimeInputWithDatePicker
                                      input={{
                                        ...field,
                                        placeholder: 'dd-MM-yyyy HH:mm:ss',
                                      }}
                                      calendar={{
                                        selected: selectedDate,
                                        onSelect: (val) => {
                                          field.onChange(
                                            format(
                                              new Date(val),
                                              'dd-MM-yyyy HH:mm:ss',
                                            ),
                                          );
                                        },
                                      }}
                                    />
                                  </FormControl>

                                  <FormDescription>
                                    Data de Limpeza Automática de Clientes tem
                                    de seguir o formato:{' '}
                                    <i>dd-MM-yyyy HH:mm:ss</i>
                                  </FormDescription>

                                  <FormMessage className="text-left" />
                                </FormItem>
                              );
                            }}
                          />

                          <FormField
                            control={formGenerateCampaignWinners.control}
                            name="confirmation"
                            render={({ field }) => (
                              <FormItem className="col-span-1 lg:col-span-2">
                                <div className="flex flex-row items-center space-x-2">
                                  <FormControl>
                                    <Checkbox
                                      className="mr-1 size-5"
                                      checked={field.value}
                                      onCheckedChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  </FormControl>
                                  <FormLabel
                                    required={true}
                                    className="ml-4 space-y-0 font-normal"
                                  >
                                    Eu concordo que esta ação tem consequências
                                    irreversíveis e que mesmo assim quero
                                    realizá-la.
                                  </FormLabel>
                                </div>

                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                          <Button
                            variant="ghost"
                            type="button"
                            onClick={() => {
                              setGenerateWinnersFormOpen(
                                !generateWinnersFormOpen,
                              );

                              formGenerateCampaignWinners.reset();
                            }}
                          >
                            <XIcon />
                            Cancelar
                          </Button>
                          <Button type="submit" variant="destructive">
                            <CheckIcon />
                            Continuar
                          </Button>
                        </div>
                      </form>
                    </Form>
                  )}
                </div>
              ) : queryCampaign.data.campaign.state ===
                  CAMPAIGN_STATES.CONCLUDED ||
                queryCampaign.data.campaign.state ===
                  CAMPAIGN_STATES.DRAW_CONCLUDED ? (
                <div className="col-span-1 flex w-full flex-col gap-12 lg:col-span-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row flex-wrap items-center justify-between pb-3">
                      <h3 className="flex flex-row items-center text-lg font-bold text-yellow-300">
                        <TrophyIcon className="mr-2 size-5" />
                        Vencedor(es)
                      </h3>

                      <DialogExportRecords
                        action={(fileType) =>
                          mutationExportWinnersData.mutate({
                            fileType,
                            winnersType: 'winners',
                          })
                        }
                        description="Exportação dos Vencedores da campanha"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {queryCampaign.data.campaign?.winners
                        ? JSON.parse(queryCampaign.data.campaign?.winners).map(
                            (item, i) => (
                              <Card
                                key={i}
                                className={cn(
                                  'col-span-1 flex flex-col gap-2 p-6 lg:col-span-2',
                                )}
                              >
                                <h4 className="pb-2">
                                  {math.increment(i, 1)}º Vencedor
                                </h4>
                                <WinnerInfo winner={item} />
                              </Card>
                            ),
                          )
                        : null}
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-row flex-wrap items-center justify-between pb-3">
                      <h3 className="flex flex-row items-center text-lg font-bold text-amber-600">
                        <TrophyIcon className="mr-2 size-5" />
                        Vencedor(es) Suplente(s)
                      </h3>

                      <DialogExportRecords
                        action={(fileType) =>
                          mutationExportWinnersData.mutate({
                            fileType,
                            winnersType: 'substitute-winners',
                          })
                        }
                        description="Exportação dos Vencedores Substitutos da campanha"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {queryCampaign.data.campaign?.winnersSubstitutes?.map(
                        (item, i) => (
                          <Card
                            key={i}
                            className={cn(
                              'col-span-1 flex flex-col gap-2 p-6 lg:col-span-2',
                            )}
                          >
                            <h4 className="pb-2">
                              {math.increment(i, 1)}º Vencedor Sub.
                            </h4>
                            <WinnerInfo winner={item} />
                          </Card>
                        ),
                      )}
                    </div>
                  </div>

                  <Link
                    target="_blank"
                    to={`/portal/${queryCampaign.data.campaign.name}/raffle`}
                  >
                    <Button>
                      <ExternalLinkIcon />
                      Ver Sorteio
                    </Button>
                  </Link>
                </div>
              ) : null}
            </FormCard>
          </div>
        ) : null}

        <div className="flex flex-row flex-wrap justify-start gap-4 pb-10 md:pb-0 lg:justify-end">
          {isAdmin ? (
            <DialogDeleteRecordConfirmation
              recordName={queryCampaign.data.campaign.name}
              redirect="/backoffice/campaigns"
              action={async () => mutationDeleteCampaign.mutateAsync()}
            />
          ) : null}

          {isAdmin ? (
            <Link to={`/backoffice/campaigns/edit/${campaignId}`}>
              <Button>
                <EditIcon />
                Editar
              </Button>
            </Link>
          ) : null}

          {queryCampaign.data.campaign.state === CAMPAIGN_STATES.ONGOING ? (
            <Link to="/backoffice/invoices/create">
              <Button>
                <PlusIcon />
                Inserir Fatura
              </Button>
            </Link>
          ) : null}

          {isAdmin &&
          queryCampaign.data.campaign.state ===
            CAMPAIGN_STATES.READY_TO_DRAW ? (
            <Link>
              <Button onClick={() => scrollToRef(refWinnerCard)}>
                <TrophyIcon />
                Gerar Vencedor(es)
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </>
  );
}
